import { useState, useEffect } from 'react';
import { AlertTriangle, BellOff, Receipt } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import { MailLanguageSelector } from '@/lib/mail/MailLanguageSelector';
import { useItineraryPlatformFeeRate } from '@/hooks/usePlatformFee';
import {
    MAIL_DEFAULT_LANGUAGE,
    type MailLanguage,
} from '@/lib/shared-proposal/hooks/useClientMailLanguage';
import {
    MANUAL_PAYMENT_METHODS,
    formatPlatformFeeRate,
    platformFeeOn,
    type ManualPaymentMethod,
} from '@/lib/payments/utils/payment-method';

interface MarkInstallmentPaidModalProps {
    installment: IInstallment | null;
    /** Itinerario della rata: l'anteprima della fee usa la sua aliquota. */
    itineraryId: string | null | undefined;
    isOpen: boolean;
    isSaving: boolean;
    formatAmount: (value: number) => string;
    formatDate: (iso: string) => string;
    onClose: () => void;
    onConfirm: (params: {
        installmentId: string;
        paymentMethod: ManualPaymentMethod;
        notifyClient: boolean;
        language: MailLanguage;
    }) => void;
    /**
     * Lingua sul profilo del cliente, per preselezionare quella delle mail dell'incasso.
     * `null` quando non la si conosce (cliente senza profilo, o chiamante che non la
     * carica): vale il ripiego.
     */
    clientProfileLanguage?: MailLanguage | null;
    isCheckingClientLanguage?: boolean;
    clientLanguageLookupFailed?: boolean;
}

/**
 * Conferma per registrare a mano l'incasso di una rata.
 *
 * Non è un doppio click di cortesia: l'operazione scrive una riga in `payments`, blocca
 * importo e scadenza della rata, e sulla prima rata fa passare l'itinerario a completato,
 * facendo scadere le proposte alternative.
 *
 * Dal 02/09/2026 la può aprire anche chi vende — TD e agenzia, non più il solo admin — e
 * la contropartita è la presa d'atto qui sotto. I metodi manuali sono per definizione
 * fuori Stripe, quindi la fee Placeefee2Be non viene trattenuta da nessuna parte e resta
 * un debito verso la piattaforma: dirlo dopo, in un riepilogo, sarebbe dirlo a operazione
 * già fatta. La spunta è obbligatoria perché il numero va letto, non scorso.
 */
export const MarkInstallmentPaidModal = ({
    installment,
    itineraryId,
    isOpen,
    isSaving,
    formatAmount,
    formatDate,
    onClose,
    onConfirm,
    clientProfileLanguage = null,
    isCheckingClientLanguage,
    clientLanguageLookupFailed,
}: MarkInstallmentPaidModalProps) => {
    const { t } = useI18n();
    const { data: feeRate, loading: feeRateLoading } = useItineraryPlatformFeeRate(isOpen ? itineraryId : null);
    const [method, setMethod] = useState<ManualPaymentMethod>('bank_transfer');
    const [feeAcknowledged, setFeeAcknowledged] = useState(false);
    // Avvisare il cliente e' il caso normale: un incasso registrato in silenzio e' la
    // scelta di chi lo ha gia' avvisato per altre vie.
    const [notifyClient, setNotifyClient] = useState(true);
    const [language, setLanguage] = useState<MailLanguage | null>(null);

    // Ogni apertura riparte dal metodo più comune e dalla spunta vuota, senza ereditare
    // nulla dalla volta prima: una presa d'atto che sopravvive alla chiusura della modale
    // non è una presa d'atto.
    useEffect(() => {
        if (isOpen) {
            setMethod('bank_transfer');
            setFeeAcknowledged(false);
            setNotifyClient(true);
            setLanguage(null);
        }
    }, [isOpen]);

    // La lingua del profilo arriva in asincrono: preselezionarla senza sovrascrivere una
    // scelta gia' fatta a mano.
    useEffect(() => {
        if (language === null && clientProfileLanguage) setLanguage(clientProfileLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientProfileLanguage]);

    if (!installment) return null;

    // Senza aliquota non si mostra un numero inventato: il blocco dice che la sta
    // caricando, e il debito vero arriva comunque dalla risposta della RPC.
    const feeAmount = feeRate ? platformFeeOn(installment.amount, feeRate.rate) : null;
    const effectiveLanguage = language ?? clientProfileLanguage ?? MAIL_DEFAULT_LANGUAGE;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            title={t('checkout.paymentPlan.markPaidModal.title')}
            footer={
                /* Niente wrapper: il footer del Modal e' gia' un flex allineato a destra
                   con il suo gap. Annidarne un altro dava due spaziature diverse fra i
                   bottoni di questa modale e quelli di tutte le altre. */
                <>
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        disabled={isSaving || !feeAcknowledged || feeRateLoading}
                        onClick={() =>
                            onConfirm({
                                installmentId: installment.id,
                                paymentMethod: method,
                                notifyClient,
                                language: effectiveLanguage,
                            })
                        }
                    >
                        {t('checkout.paymentPlan.markPaidModal.confirm')}
                    </Button>
                </>
            }
        >
            {/* Il Modal non mette padding sul contenuto: sta a ogni modale allinearsi ai
                px-6 py-4 di header e footer, come fa l'editor del piano. Senza, il
                riepilogo della rata toccava i bordi del pannello. */}
            <div className="flex flex-col gap-4 px-6 py-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                    <span className="font-medium text-gray-900">{installment.label}</span>
                    <div className="flex items-baseline justify-between">
                        <span className="text-sm text-gray-500">
                            {t('checkout.paymentPlan.dueOn', { date: formatDate(installment.due_date) })}
                        </span>
                        <span className="text-lg font-semibold text-gray-900 tabular-nums">
                            {formatAmount(installment.amount)}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="manual-payment-method">
                        {t('checkout.paymentPlan.markPaidModal.method')}
                    </Label>
                    <Select value={method} onValueChange={(value) => setMethod(value as ManualPaymentMethod)}>
                        <SelectTrigger id="manual-payment-method">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {MANUAL_PAYMENT_METHODS.map((value) => (
                                <SelectItem key={value} value={value}>
                                    {t(`checkout.paymentPlan.markPaidModal.methods.${value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Avviso al cliente. Sta sopra il blocco della commissione perche' e'
                    una scelta, non una presa d'atto: chi registra un bonifico gia'
                    annunciato al telefono non deve mandare una mail che sembra un
                    duplicato. */}
                <div className="flex flex-col gap-3">
                    <label
                        htmlFor="notify-client"
                        className="flex items-start gap-2 cursor-pointer text-sm text-gray-700"
                    >
                        <Checkbox
                            id="notify-client"
                            checked={notifyClient}
                            onCheckedChange={(checked) => setNotifyClient(checked === true)}
                            className="mt-0.5"
                        />
                        <span>
                            {t('checkout.paymentPlan.markPaidModal.notifyClient')}
                        </span>
                    </label>

                    {notifyClient ? (
                        <MailLanguageSelector
                            id="mark-paid-mail-language"
                            value={effectiveLanguage}
                            onChange={setLanguage}
                            profileLanguage={clientProfileLanguage}
                            isLoading={isCheckingClientLanguage}
                            lookupFailed={clientLanguageLookupFailed}
                        />
                    ) : (
                        <p className="flex items-start gap-2 text-xs text-gray-500">
                            <BellOff className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                            <span>{t('checkout.paymentPlan.markPaidModal.notifyClientOff')}</span>
                        </p>
                    )}
                </div>

                {/* Il blocco della commissione sta sopra quello sulle conseguenze
                    sull'itinerario: è la parte che l'utente non si aspetta, e in fondo
                    alla modale verrebbe letta dopo aver già deciso. */}
                <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                        <Receipt className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-amber-900">
                                {t('checkout.paymentPlan.markPaidModal.fee.title')}
                            </span>
                            <p className="text-sm text-amber-900">
                                {feeRate && feeAmount !== null
                                    ? t('checkout.paymentPlan.markPaidModal.fee.body', {
                                        fee: formatAmount(feeAmount),
                                        rate: formatPlatformFeeRate(feeRate.rate),
                                    })
                                    : feeRateLoading
                                        ? t('checkout.paymentPlan.markPaidModal.fee.loading')
                                        : t('checkout.paymentPlan.markPaidModal.fee.unavailable')}
                            </p>
                        </div>
                    </div>

                    <label
                        htmlFor="platform-fee-ack"
                        className="flex items-start gap-2 cursor-pointer text-sm text-amber-900"
                    >
                        <Checkbox
                            id="platform-fee-ack"
                            checked={feeAcknowledged}
                            onCheckedChange={(checked) => setFeeAcknowledged(checked === true)}
                            className="mt-0.5 border-amber-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                        />
                        <span>
                            {feeAmount !== null
                                ? t('checkout.paymentPlan.markPaidModal.fee.acknowledge', {
                                    amount: formatAmount(installment.amount),
                                    fee: formatAmount(feeAmount),
                                })
                                : t('checkout.paymentPlan.markPaidModal.fee.acknowledgeNoAmount', {
                                    amount: formatAmount(installment.amount),
                                })}
                        </span>
                    </label>
                </div>

                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-900">
                        {t('checkout.paymentPlan.markPaidModal.warning')}
                    </p>
                </div>
            </div>
        </Modal>
    );
};
